// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'utils/day';
import ImagePlaceholder from '../../../components/image-placeholder';
import {
  Apartment,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from '@mui/icons-material';
import styles from './styles.module.scss';

type Props = {
  results: Talent[],
  index: number,
  value: string,
  showAll: boolean,
  onSubmit: (e: any) => void,
  onShowAll: () => void,
  listNotice?: null | 'MIN_CHARS' | 'NO_RESULTS',
  loading: boolean,
  hasMore: boolean,
  searchError: boolean,
  onLoadMore: () => void,
  onRetry: () => void,
};

const Suggestions = ({
  results,
  index,
  onSubmit,
  value,
  showAll,
  onShowAll,
  listNotice = null,
  loading,
  hasMore,
  searchError,
  onLoadMore,
  onRetry,
}: Props) => {
  const { t } = useTranslation();

  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  const showAllRowStyles = [
    results.length
      ? styles.search_results_show_all_wrapper
      : styles.search_results_show_all_wrapper_alone,
  ];
  if (index === results.length) {
    showAllRowStyles.push(styles.active);
  }

  const scrollRef = React.useRef(null);
  const [scrollHints, setScrollHints] = React.useState({
    above: false,
    below: false,
  });
  const updateScrollHints = React.useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const above = container.scrollTop > 1;
    const below =
      container.scrollHeight - container.scrollTop - container.clientHeight >
        1 || hasMore;
    setScrollHints(previous =>
      previous.above === above && previous.below === below
        ? previous
        : { above, below }
    );
  }, [hasMore]);

  React.useLayoutEffect(() => {
    updateScrollHints();
    window.addEventListener('resize', updateScrollHints);
    return () => window.removeEventListener('resize', updateScrollHints);
  }, [updateScrollHints, results.length, loading, searchError]);
  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setHoveredIndex(null);
  }, [value]);

  React.useEffect(() => {
    const container = scrollRef.current;
    const row = container?.querySelector(`[data-search-index="${index}"]`);
    if (!container || !row) return;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < container.scrollTop) container.scrollTop = top;
    else if (bottom > container.scrollTop + container.clientHeight) {
      container.scrollTop = bottom - container.clientHeight;
    }
  }, [index]);

  return (
    <div className={styles.search_results}>
      {listNotice === 'MIN_CHARS' && (
        <div
          className={[
            styles.search_result_row,
            styles.search_result_row_notice,
          ].join(' ')}
          role="status"
        >
          <span className={styles.search_result_text}>
            {t('sentences:search_min_chars')}
          </span>
        </div>
      )}
      {listNotice === 'NO_RESULTS' && (
        <div
          className={[
            styles.search_result_row,
            styles.search_result_row_notice,
          ].join(' ')}
          role="status"
        >
          <span className={styles.search_result_text}>
            {t('sentences:search_no_results')}
          </span>
        </div>
      )}
      <div className={styles.search_results_viewport}>
        {scrollHints.above && (
          <div
            className={`${styles.search_scroll_hint} ${styles.search_scroll_hint_top}`}
            aria-hidden="true"
          >
            <KeyboardArrowUp />
          </div>
        )}
        <div
          ref={scrollRef}
          className={styles.search_results_scroll}
          onScroll={event => {
            updateScrollHints();
            const { scrollTop, scrollHeight, clientHeight } =
              event.currentTarget;
            if (scrollHeight - scrollTop - clientHeight < 96) onLoadMore();
          }}
        >
          {results.map((r, i) => {
            const rowStyles = [styles.search_result_row];
            if (index === i || hoveredIndex === i) {
              rowStyles.push(styles.active);
            }

            const handleClick = () => onSubmit(r);
            const handleMouseEnter = () => setHoveredIndex(i);
            const handleMouseLeave = () => setHoveredIndex(null);
            const updatedDate = r.validation.lastUpdatedAt
              ? dayjs(r.validation.lastUpdatedAt)
              : null;
            const dateLabel = updatedDate?.isValid()
              ? updatedDate.locale('en').format('MMMM D, YYYY')
              : '—';
            const submitter = r.validation.validatedBy?.[0];
            const submitterName =
              [submitter?.firstName, submitter?.lastName]
                .filter(Boolean)
                .join(' ')
                .trim() || '—';
            const submitterLabel =
              submitter?.isActive === false
                ? `${submitterName} (${t('sentences:inactive')})`
                : submitterName;

            return (
              <div
                className={rowStyles.join(' ')}
                key={r.validation.uuid}
                data-search-index={i}
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <div className={styles.search_result_image_holder}>
                  {r.validation.type === 'LEGAL_ENTITY' ? (
                    <div
                      style={{
                        marginLeft: '5px',
                        borderRadius: '50px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '30px',
                        width: '30px',
                        borderStyle: 'solid',
                        borderWidth: '1px',
                        borderColor: '#E1E1E1',
                        background: 'white',
                      }}
                    >
                      <Apartment style={{ color: '#908E8C' }} />
                    </div>
                  ) : (
                    <ImagePlaceholder
                      src={r?.user?.picture}
                      alt="search picture"
                    />
                  )}
                </div>
                <div className={styles.search_result_details}>
                  <div className={styles.search_result_heading}>
                    <span className={styles.search_result_text}>
                      {r.validation.type === 'LEGAL_ENTITY'
                        ? r.name
                        : `${r.firstName} ${r.lastName}`}
                    </span>
                    {r.validation.isArchived && (
                      <span className={styles.search_result_email}>
                        {' [ARCHIVED]'}
                      </span>
                    )}
                  </div>
                  <div className={styles.search_result_metadata}>
                    <span
                      className={styles.search_result_date}
                      title={`${t('verbs.last_validated')}: ${dateLabel}`}
                    >
                      {dateLabel}
                    </span>
                    <span
                      className={styles.search_result_separator}
                      aria-hidden="true"
                    >
                      ·
                    </span>
                    <span
                      className={styles.search_result_submitter}
                      title={`${t('nouns.submitter')}: ${submitterLabel}`}
                    >
                      {submitterLabel}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          {loading && (
            <div role="status" className={styles.search_result_row}>
              {t('sentences:loading')}
            </div>
          )}
          {searchError && (
            <div role="status" className={styles.search_result_row}>
              {t('sentences:search_load_error')}
              <button type="button" onClick={onRetry}>
                {t('sentences:search_retry')}
              </button>
            </div>
          )}
        </div>
        {scrollHints.below && (
          <div
            className={`${styles.search_scroll_hint} ${styles.search_scroll_hint_bottom}`}
            aria-hidden="true"
          >
            <KeyboardArrowDown />
          </div>
        )}
      </div>
      {value && showAll && (
        <div className={showAllRowStyles.join(' ')} onClick={onShowAll}>
          <span className={styles.search_results_show_all}>
            {t('sentences:search_show_all', { value })}
          </span>
        </div>
      )}
    </div>
  );
};

export default Suggestions;
